import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ErrorState } from '@/components/error-state'
import { KuniriLogo } from '@/components/kuniri-logo'
import { LoadingState } from '@/components/loading-state'

import { ActivityForm } from '../activity-form'
import { activityComposerApi } from '../api'
import { Notice } from '../components/notice'
import type { ProfileCheck } from '../components/profile-gate'
import type { Category } from '../types'

type CategoryState =
  | { status: 'loading' }
  | { status: 'ready'; items: Category[] }
  | { status: 'error'; error: unknown }

/**
 * `/create` (UI_PARITY §3.10). Categories and the profile gate load
 * independently: a profile-check failure keeps the form usable and the server
 * remains authoritative, while a confirmed missing avatar blocks publishing
 * with a visible reason and a link to the profile.
 */
export function CreateActivityScreen() {
  const { t } = useTranslation()
  const [categories, setCategories] = useState<CategoryState>({
    status: 'loading',
  })
  const [profile, setProfile] = useState<ProfileCheck>({ status: 'loading' })
  const [categoryAttempt, setCategoryAttempt] = useState(0)
  const [profileAttempt, setProfileAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    activityComposerApi
      .listCategories({ signal: controller.signal })
      .then((items) => {
        if (controller.signal.aborted) return
        setCategories({ status: 'ready', items })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setCategories({ status: 'error', error })
      })
    return () => controller.abort()
  }, [categoryAttempt])

  useEffect(() => {
    const controller = new AbortController()
    activityComposerApi
      .getProfile({ signal: controller.signal })
      .then((value) => {
        if (controller.signal.aborted) return
        setProfile({ status: 'ready', profile: value })
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setProfile({ status: 'error' })
      })
    return () => controller.abort()
  }, [profileAttempt])

  function retryProfile(): void {
    setProfile({ status: 'loading' })
    setProfileAttempt((value) => value + 1)
  }

  return (
    <section aria-labelledby="create-title" className="content-width pb-10">
      <header className="compact-sticky-header flex min-h-12 items-center py-1 min-[768px]:hidden">
        <Link to="/home" className="inline-flex min-h-11 items-center">
          <KuniriLogo height={24} />
        </Link>
      </header>

      <h1 id="create-title" className="sr-only">
        {t('screens.create.title')}
      </h1>

      {categories.status === 'loading' ? (
        <LoadingState variant="form" labelKey="activityForm.loading" />
      ) : categories.status === 'error' ? (
        <div className="mt-5">
          <ErrorState
            error={categories.error}
            titleKey="activityForm.errors.categoriesTitle"
            bodyKey="activityForm.errors.categories"
            onRetry={() => {
              setCategories({ status: 'loading' })
              setCategoryAttempt((value) => value + 1)
            }}
          />
        </div>
      ) : (
        <div className="mt-3">
          {categories.items.length === 0 ? (
            <div className="mb-4">
              <Notice tone="error">
                {t('activityForm.errors.categoriesEmpty')}
              </Notice>
            </div>
          ) : null}
          <ActivityForm
            mode="create"
            categories={categories.items}
            profile={profile}
            onProfileRetry={retryProfile}
          />
        </div>
      )}
    </section>
  )
}
